/**
 * Devices List
 * View and manage device instances
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchDevices } from './api';
import { Link } from 'react-router-dom';
import type { Device } from './types';
import {
  classifyDevicePresence,
  countDevicePresence,
  lastSeenDisplay,
  presenceHeadline,
  type DevicePresence,
} from './devicePresenceHonesty';

export default function DevicesList() {
  const { data: devices = [], isLoading, error } = useQuery({
    queryKey: ['iot', 'devices'],
    queryFn: fetchDevices,
    refetchInterval: 15000, // Refresh every 15s
  });

  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'online' | 'offline' | 'unknown'>('all');

  const filteredDevices = useMemo(() => {
    return devices.filter((device) => {
      const matchesQuery = query.trim().length === 0 ||
        device.deviceId?.toLowerCase().includes(query.toLowerCase()) ||
        device.type?.toLowerCase().includes(query.toLowerCase()) ||
        device.gatewayId?.toLowerCase().includes(query.toLowerCase());
      const matchesStatus =
        statusFilter === 'all' || classifyDevicePresence(device) === statusFilter;
      return matchesQuery && matchesStatus;
    });
  }, [devices, query, statusFilter]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Failed to load devices: {String(error)}</p>
      </div>
    );
  }

  const presenceCounts = countDevicePresence(devices);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Devices</h1>
          <p className="text-gray-600 mt-1">
            {presenceHeadline(presenceCounts)}
          </p>
        </div>
        <Link
          to="/iotshack/agent"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium shadow-sm hover:shadow-md hover:from-blue-500 hover:to-indigo-500"
        >
          🤖 Add Device with IoT Agent
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between shadow-sm">
        <div className="flex flex-1 items-center gap-3">
          <div className="relative w-full">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by device ID, type, or gateway..."
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs text-gray-500">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            <option value="all">All</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
            <option value="unknown">Unknown</option>
          </select>
        </div>
      </div>

      {/* Devices Cards */}
      {filteredDevices.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center shadow-sm">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Devices Found</h3>
          <p className="text-gray-600 mb-4">Use the IoT Agent to register new devices</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredDevices.map((device) => (
            <DeviceCard key={device._id} device={device} />
          ))}
        </div>
      )}
    </div>
  );
}

function DeviceCard({ device }: { device: Device }) {
  return (
    <div className="bg-gradient-to-br from-white to-blue-50/30 rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5">
      <div className="flex items-center justify-between">
        <div>
          <Link
            to={`/iotshack/devices/${device.deviceId}`}
            className="text-sm font-semibold text-blue-700 hover:text-blue-800"
          >
            {device.deviceId}
          </Link>
          <p className="text-xs text-gray-500 mt-1">{device.type || 'Unknown type'}</p>
        </div>
        <StatusPill status={classifyDevicePresence(device)} />
      </div>

      <div className="mt-4 space-y-2 text-xs text-gray-600">
        <div className="flex justify-between">
          <span>Gateway</span>
          <span className="text-gray-900">{device.gatewayId || '—'}</span>
        </div>
        <div className="flex justify-between">
          <span>Last Seen</span>
          <span className="text-gray-900">
            {lastSeenDisplay(device.lastSeen)}
          </span>
        </div>
        <div className="flex justify-between">
          <span>Decoder</span>
          <span className="text-gray-900">{device.decoderId || '—'}</span>
        </div>
      </div>

      <div className="mt-4">
        <Link
          to={`/iotshack/devices/${device.deviceId}`}
          className="inline-flex items-center text-xs font-medium text-blue-700 hover:text-blue-800"
        >
          View dataflow
        </Link>
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: DevicePresence }) {
  const statusColors = {
    online: 'bg-emerald-100 text-emerald-800',
    offline: 'bg-rose-100 text-rose-800',
    unknown: 'bg-gray-100 text-gray-800',
  };
  return (
    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusColors[status]}`}>
      {status}
    </span>
  );
}

